import { Skeleton, SkeletonGroup } from "./Skeleton.js";

/** Desktop placeholders for the lazily loaded Mail and Calendar pages.
 *
 *  The only Suspense fallbacks were the phone skeletons, which are
 *  `md:hidden`, so on a desktop the whole pane was blank while the page's
 *  chunk downloaded. These are shaped like the desktop pages instead, so the
 *  arrival reads as a fill rather than a jump. */

export function DesktopMailSkeleton(): JSX.Element {
  return (
    <SkeletonGroup
      label="Loading mail"
      className="fixed top-0 left-[var(--bv2-sidebar-w,220px)] right-0 bottom-0 hidden md:flex gap-2 p-2 bg-slate-100 dark:bg-slate-950"
    >
      <div className="w-[288px] flex-shrink-0 rounded-xl bg-slate-50 dark:bg-slate-900 p-3 space-y-3">
        <Skeleton className="h-7 w-32" />
        {[70, 55, 62, 48, 66, 52].map((w, i) => (
          <Skeleton key={i} className="h-4" style={{ width: `${w}%` }} />
        ))}
      </div>
      <div className="w-[440px] flex-shrink-0 rounded-xl bg-white dark:bg-slate-900 p-3 space-y-4">
        <Skeleton className="h-8 w-full" />
        {[88, 72, 80, 64, 84, 70, 78].map((w, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-9 w-9 flex-shrink-0 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-3" style={{ width: `${w}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="flex-1 rounded-xl bg-white dark:bg-slate-900 p-5 space-y-3">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
        {[100, 92, 96, 70, 88].map((w, i) => (
          <Skeleton key={i} className="h-3" style={{ width: `${w}%` }} />
        ))}
      </div>
    </SkeletonGroup>
  );
}

export function DesktopCalendarSkeleton(): JSX.Element {
  return (
    <SkeletonGroup label="Loading calendar" className="hidden md:flex min-h-screen gap-0">
      <div className="w-56 flex-shrink-0 border-r border-slate-200 dark:border-slate-800 p-3 space-y-2">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-7 w-full" />
        <Skeleton className="h-7 w-full" />
      </div>
      <div className="flex-1 p-4 space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full rounded-xl" />
        {[64, 88, 48, 72].map((h, i) => (
          <Skeleton key={i} className="w-full rounded-md" style={{ height: h }} />
        ))}
      </div>
    </SkeletonGroup>
  );
}
